import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import { findIndex, get } from 'lodash';

import ModalWrapper from 'modules/shared/ModalWrapper';
import { useModal } from 'modules/shared/hooks';
import YesNoModal from 'modules/shared/YesNoModal';
import TopBorderBtn from 'modules/shared/TopBorderBtn';
import { clearReportPhoto } from 'modules/security/containers/ReportsContainer/services/actions';
import { branchTitleSelector } from 'modules/common/LogoutBtn/selectors';
import { RolesMode } from '../../../../../entities/Roles';
import { Image as IMG, SliderImage } from '../../../../../entities/Photo/interfaces';
import { tripDetailsType, tripStatuses, TripStatusIds } from '../../../../../entities/Trip';

import Carousel from '../Carousel';
import styles from './PhotoGrid.module.scss';

type UpploadImg = {
  type: string;
  docIndex: number | null;
  picIndex: number;
  docNumber: string;
  docType: number;
  file: File;
  id: any;
};

type Props = {
  serverImages: SliderImage[];
  statusId: TripStatusIds;
  mode: RolesMode | 'reports';
  setFieldValue?: (name: string, files: any) => void;
  isPhotoError?: any;
  typeCard: tripDetailsType;
  values?: any;
  errors?: any;
  images?: UpploadImg[];
  activeIndex?: string;
};

const PhotoGrid: React.FC<Props> = ({
  mode,
  serverImages,
  images,
  statusId,
  setFieldValue,
  values,
  typeCard,
  activeIndex = 0,
}) => {
  const dispatch = useDispatch();
  const branchTitle = useSelector(branchTitleSelector);
  const useVisitorDoc: boolean = branchTitle?.useVisitorDoc ?? true;
  const useTransportDoc: boolean = branchTitle?.useTransportDoc ?? true;

  const [index, setIndex] = useState<number>(0);
  const [numDoctype1, setNumDoctype1] = useState<number>(0);
  const [numDoctype3, setNumDoctype3] = useState<number>(0);
  const [trailerDoc, setTrailerDoc] = useState<boolean>(false);
  const [vehicleDoc, setVehicleDoc] = useState<boolean>(false);
  const [previews, setPreviews] = useState<SliderImage[]>([]);
  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowZoom, openModal: openZoom, closeModal: closeZoom } = useModal();
  const canvasRef = useRef();

  const isSecurity = mode === 'security';
  const isDirector = mode === 'director';
  const isReports = mode === 'reports';

  const showForSecurity = [tripStatuses.create, tripStatuses.parkingInLine].includes(statusId);
  const isDisabled = (isSecurity && !showForSecurity) || isReports || isDirector;
  const previewImgs = previews.filter((img) => !img.isDeleted);
  const isForwarder =
    typeCard === 'forwarder' &&
    (!!values.forwarder?.lastName ||
      !!values.forwarder?.firstName ||
      !!values.forwarder?.phone?.number ||
      (!!values.forwarder?.documents?.length && values.forwarder?.documents[0]?.number) ||
      !!values.forwarder?.middleName);

  const deleteHandler = () => {
    const { isLocal, id, type, docIndex, picIndex } = previewImgs[index];

    const isVehicle = type === 'vehicle';
    const isTrailer = type === 'trailer';

    let name = '';
    const picName = isLocal ? 'pictureFiles' : 'pictures';

    if (isVehicle || isTrailer) {
      name = `${type}.document.${picName}`;
    } else {
      name = `${type}.documents[${docIndex}].${picName}`;
    }
    const pictures = get(values, name, []);

    if (isLocal) {
      // @ts-ignore
      setFieldValue(name, [...pictures.slice(0, picIndex), ...pictures.slice(picIndex + 1)]);
    } else {
      // @ts-ignore
      setFieldValue(
        name,
        pictures.map((img: IMG) => {
          if (img.id === id) {
            return { ...img, isDeleted: true };
          }

          return img;
        }),
      );
    }

    const nexIdx = index >= previewImgs.length - 1 ? previewImgs.length - 2 : index;
    setIndex(nexIdx);
    closeModal();

    if (previewImgs.length === 1) closeZoom();
  };

  const handleHideZoom = () => {
    if (isReports) {
      dispatch(clearReportPhoto());
    }

    closeZoom();
  };

  // красный текст с недостающими документами
  const numberDriverDoc1 = () => {
    const docsLicence = values[typeCard].documents.find((d: any) => d.type === 1);

    if (!docsLicence) return 0;
    let number = docsLicence.pictures.filter((doc: any) => !doc.isDeleted).length;

    // eslint-disable-next-line
    if (!!docsLicence.pictureFiles) number = docsLicence.pictureFiles.length + number;

    return setNumDoctype1(2 - number);
  };

  const numberDriverDoc3 = () => {
    const docsPassword = values[typeCard]?.documents?.find((d: any) => d?.type === 3);

    if (!docsPassword) return 2;
    let number = docsPassword.pictures.filter((doc: any) => !doc.isDeleted).length || 0;

    // eslint-disable-next-line
    if (!!docsPassword.pictureFiles) number = docsPassword.pictureFiles.length + number;

    return setNumDoctype3(2 - number);
  };

  const getTrailerDocs = () => {
    if (typeCard === 'vehicle') {
      if (!values.trailer?.document?.number?.length) return setTrailerDoc(false);

      if (
        values.trailer.document?.pictures &&
        values.trailer.document?.pictures.filter((doc: any) => !doc?.isDeleted).length
      )
        return setTrailerDoc(false);

      if (values.trailer?.document?.pictureFiles?.length) return setTrailerDoc(false);

      return setTrailerDoc(true);
    }
  };
  const getVehicleDocs = () => {
    if (typeCard === 'vehicle') {
      if (
        values.vehicle?.document?.pictures &&
        values.vehicle?.document?.pictures.filter((doc: any) => !doc.isDeleted).length
      )
        return setVehicleDoc(false);

      if (values.vehicle?.document?.pictureFiles && values.vehicle?.document?.pictureFiles?.length)
        return setVehicleDoc(false);

      return setVehicleDoc(true);
    }
  };

  useEffect(() => {
    if (isReports) {
      openZoom();
    }
  }, []);

  useEffect(() => {
    const imgs = [];

    if (images) {
      // eslint-disable-next-line
      for (const { type, docIndex, picIndex, docType, docNumber, file } of images) {
        if (!file) return;
        const windowUrl = window.URL || window.webkitURL;

        imgs.push({
          id: Math.floor(1 + Math.random() * 100),
          isLocal: true,
          isDeleted: false,
          rotated: 0,
          type,
          docIndex,
          picIndex,
          docType,
          docNumber,
          name: file.name,
          picture: windowUrl.createObjectURL(file),
        });
      }
    }

    const newPreview = [...serverImages, ...imgs];

    if (previews.length) {
      if (previews.length > newPreview.length) {
        setIndex(0);
      } else {
        const dif = newPreview.filter((item) => !previews.some((img) => img.name === item.name));

        const allDif = dif.filter((item) => !item.isDeleted);
        const withoutDel = newPreview.filter((item) => !item.isDeleted);

        if (allDif.length) {
          setIndex(Math.abs(withoutDel.indexOf(allDif[0])));
        }
      }
    }

    // красный текст с недостающими документами
    if (typeCard === 'driver') numberDriverDoc1();
    if (typeCard === 'driver') numberDriverDoc3();
    if (typeCard === 'forwarder') numberDriverDoc3();
    if (typeCard === 'vehicle') {
      getTrailerDocs();
      getVehicleDocs();
    }

    setPreviews(newPreview);
  }, [serverImages, images, typeCard, isForwarder]);

  // переключаем слайд
  useEffect(() => {
    if (!!activeIndex && previews.length) {
      const elem = previews.find((prev) => prev.docNumber === activeIndex);

      setIndex(elem ? previews.indexOf(elem) : index);
    }
  }, [activeIndex]);

  return (
    <>
      {!isReports && (
        <Col xs={12} sm={12} lg={4} className={styles.mh310}>
          <Row className={styles.wrapper}>
            {typeCard === 'driver' && useVisitorDoc && numDoctype1 > 0 ? (
              <p className="w-100 mb-0 text-center font-weight-bold text-danger">
                Додайте {numDoctype1} фото посвідчення водія
              </p>
            ) : null}
            {typeCard === 'driver' &&
              values[typeCard].documents[findIndex(values[typeCard].documents, (doc: any) => doc.type === 3)]?.number &&
              numDoctype3 > 0 && (
                <p className="w-100 mb-0 text-center font-weight-bold text-danger">
                  Додайте {numDoctype3} фото паспорта водія
                </p>
              )}
            {typeCard === 'forwarder' && isForwarder && numDoctype3 > 0 && (
              <p className="w-100 mb-0 text-center font-weight-bold text-danger">
                Додайте {numDoctype3} фото паспорта експедитора
              </p>
            )}
            {typeCard === 'vehicle' && useTransportDoc && vehicleDoc && (
              <p className="w-100 mb-0 text-center font-weight-bold text-danger">Додайте фото техпаспорта автомобіля</p>
            )}
            {typeCard === 'vehicle' && trailerDoc && (
              <p className="w-100 mb-0 text-center font-weight-bold text-danger">Додайте фото техпаспорта причепа</p>
            )}
            <Col
              className={classNames(
                'd-flex flex-column justify-content-end align-items-center text-white mt-2',
                styles.imgWrap,
              )}
            >
              {previewImgs && previewImgs.length ? (
                <Carousel index={index} setIndex={setIndex} images={previewImgs} openZoomHandler={openZoom} />
              ) : null}
            </Col>
          </Row>
        </Col>
      )}
      {isShowZoom && (
        <ModalWrapper
          contentClassName={styles.modal}
          show
          onHide={handleHideZoom}
          size="lg"
          headerBtn={
            <div>
              <a href={previewImgs[index]?.picture} download>
                <TopBorderBtn icon="download" className="mt-2" />
              </a>
              <TopBorderBtn
                icon="delete"
                className="mt-2"
                {...(previewImgs && previewImgs.length && { clickHandler: openModal })}
                disabled={isDisabled}
              />
            </div>
          }
        >
          <div className="d-flex align-items-center flex-column">
            {/* @ts-ignore */}
            <canvas ref={canvasRef} id="canvas" className={styles.crop} />
            <Carousel index={index} setIndex={setIndex} images={previewImgs} withControls />
          </div>
        </ModalWrapper>
      )}
      <YesNoModal isShow={isShow} title="Видалити зображення" closeModal={closeModal} acceptHandler={deleteHandler} />
    </>
  );
};

export default PhotoGrid;
